import React from 'react';
import { MapPin, Clock, Phone, Mail, CheckCircle2, Star, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import SEOHelmet from '../../components/SEOHelmet';
import LocalBusinessSchema from '../../components/LocalBusinessSchema';
import RelatedCities from '../../components/RelatedCities';
import { companyInfo } from '../../mockData';

const PalmSpringsPage = () => {
  return (
    <div style={{ backgroundColor: 'var(--bg-page)', minHeight: '100vh' }}>
      <SEOHelmet 
        title="Process Server Palm Springs CA 92262 | Same Day Legal Document Service | Attorney Support"
        description="Professional process server in Palm Springs, CA 92262. Same-day service for attorneys, law firms & businesses. Riverside County Court filing, skip tracing, document retrieval. Licensed, bonded, insured. Request a quote online."
        keywords="process server Palm Springs CA 92262, Palm Springs process serving, Riverside County Superior Court, legal document service Palm Springs, attorney service Palm Springs, court filing 92262, skip tracing Palm Springs"
        canonical="/cities/palm-springs"
      />
      <LocalBusinessSchema 
        pageType="city"
        serviceArea="Palm Springs"
        specificServices={["LegalService", "ProcessServing"]}
      />
      <Header />

      {/* Hero Section */}
      <section style={{ backgroundColor: 'var(--bg-page)', padding: '160px 0 80px 0' }}>
        <div className="container">
          <div style={{ textAlign: 'center', maxWidth: '800px', margin: '0 auto' }}>
            <h1 className="brand-display" style={{
              marginBottom: '1.5rem',
              color: 'var(--text-primary)'
            }}>
              Process Server Palm Springs, California
            </h1>
            <p className="body-large" style={{
              color: 'var(--text-secondary)',
              marginBottom: '2rem',
              lineHeight: '1.6'
            }}>
              Premier legal document services in Palm Springs, CA 92262-92262. Professional, registered and bonded process servers serving the heart of the Coachella Valley with flexible service options. We serve the historic downtown district, upscale resorts along Palm Canyon Drive, the Desert Fashion Plaza area, and residential neighborhoods from the Racquet Club Road corridor to East Palm Springs.
            </p>
            
            <div style={{
              backgroundColor: 'var(--bg-card)',
              padding: '1.5rem',
              borderRadius: '8px',
              border: '1px solid var(--border-light)',
              marginBottom: '2rem',
              textAlign: 'left'
            }}>
              <h3 style={{ color: 'var(--brand-primary)', marginBottom: '1rem', fontSize: '1.1rem' }}>
                🏛️ Palm Springs Local Court Information
              </h3>
              <p style={{ color: 'var(--text-primary)', marginBottom: '0.5rem' }}>
                <strong>Riverside County Superior Court - Palm Springs Branch</strong><br />
                3255 E Tahquitz Canyon Way, Palm Springs, CA 92262
              </p>
              <p style={{ color: 'var(--text-secondary)', fontSize: '0.9rem' }}>
                Local Palm Springs courthouse for traffic, small claims, and local matters. For major civil and family cases, we also serve the main Indio courthouse (Larson Justice Center).
              </p>
            </div>

            <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center', flexWrap: 'wrap' }}>
              <Link to="/place-order" className="btn-primary" style={{ textDecoration: 'none' }}>
                Place Order Now
              </Link>
              <Link to="/quote" className="btn-secondary" style={{ textDecoration: 'none' }}>
                Get Free Quote
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Services & Local Info */}
      <section style={{ backgroundColor: 'var(--bg-card)', padding: '80px 0' }}>
        <div className="container">
          <div style={{ textAlign: 'center', marginBottom: '3rem' }}>
            <h2 className="heading-1" style={{ marginBottom: '1rem' }}>
              Palm Springs Legal Document Services
            </h2>
            <p className="body-large" style={{ color: 'var(--text-secondary)', maxWidth: '600px', margin: '0 auto' }}>
              Serving Palm Springs and the greater Coachella Valley with comprehensive legal support services.
            </p>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '2rem' }}>
            {/* Services */}
            <div style={{
              backgroundColor: 'var(--bg-page)',
              padding: '2rem',
              borderRadius: '12px',
              border: '1px solid var(--border-medium)'
            }}>
              <h3 className="heading-3" style={{ marginBottom: '1.5rem', display: 'flex', alignItems: 'center' }}>
                <CheckCircle2 size={24} color="var(--brand-primary)" style={{ marginRight: '0.5rem' }} />
                Professional Services
              </h3>
              <ul style={{ listStyle: 'none', padding: 0 }}>
                {[
                  'Process Serving & Legal Notices',
                  'Court Document Filing',
                  'County Recording Services',
                  'Document Retrieval & Research',
                  'Skip Tracing & Locate Services'
                ].map((service, index) => (
                  <li key={service} style={{ 
                    padding: '0.75rem 0',
                    borderBottom: index < 4 ? '1px solid var(--border-light)' : 'none',
                    display: 'flex',
                    alignItems: 'center'
                  }}>
                    <CheckCircle2 size={16} color="var(--brand-primary)" style={{ marginRight: '0.75rem' }} />
                    <span style={{ color: 'var(--text-primary)' }}>{service}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Palm Springs Advantages */}
            <div style={{
              backgroundColor: 'var(--bg-page)',
              padding: '2rem',
              borderRadius: '12px',
              border: '1px solid var(--border-medium)'
            }}>
              <h3 className="heading-3" style={{ marginBottom: '1.5rem', display: 'flex', alignItems: 'center' }}>
                <Star size={24} color="var(--brand-primary)" style={{ marginRight: '0.5rem' }} />
                Palm Springs Advantages
              </h3>
              <div style={{ color: 'var(--text-primary)', lineHeight: '1.6' }}>
                <p style={{ marginBottom: '1rem' }}>
                  <strong>Resort District Expertise</strong><br />
                  <span style={{ color: 'var(--text-secondary)' }}>Specialized service for luxury hotels like Two Bunch Palms, Parker Palm Springs, and Desert Hot Springs Resort Collection</span>
                </p>
                <p style={{ marginBottom: '1rem' }}>
                  <strong>Downtown Business Core</strong><br />
                  <span style={{ color: 'var(--text-secondary)' }}>Palm Canyon Drive, Indian Canyon Drive, and Tahquitz Canyon Way commercial district experience</span>
                </p>
                <p style={{ marginBottom: '1rem' }}>
                  <strong>Medical & Professional Plaza</strong><br />
                  <span style={{ color: 'var(--text-secondary)' }}>Desert Regional Medical Center area, professional offices, and medical groups</span>
                </p>
                <p style={{ marginBottom: '1rem' }}>
                  <strong>Residential Communities</strong><br />
                  <span style={{ color: 'var(--text-secondary)' }}>Tennis Club, El Mirador, Las Palmas, and Andreas Hills neighborhoods. ZIP codes 92262, 92263, 92262</span>
                </p>
                <p style={{ color: 'var(--text-secondary)', fontSize: '0.9rem', fontStyle: 'italic' }}>
                  Deep knowledge of Palm Springs municipal court procedures, city offices, and local business community requirements.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Service Levels */}
      <section style={{ backgroundColor: 'var(--bg-page)', padding: '80px 0' }}>
        <div className="container">
          <div style={{ textAlign: 'center', marginBottom: '3rem' }}>
            <h2 className="heading-1" style={{ marginBottom: '1rem' }}>
              Service Options in Palm Springs
            </h2>
            <p className="body-large" style={{ color: 'var(--text-secondary)', maxWidth: '700px', margin: '0 auto' }}>
              Flexible service levels to meet your Palm Springs legal document needs, from routine to urgent.
            </p>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '2rem' }}>
            {[
              {
                icon: <Clock size={32} />,
                title: 'Routine Service',
                description: '4 business days - Standard processing for non-urgent legal documents.',
                timeline: '4 Days'
              },
              {
                icon: <Clock size={32} />,
                title: 'Priority Service',
                description: 'Next business day - Expedited processing for time-sensitive matters.',
                timeline: 'Next Day'
              },
              {
                icon: <Clock size={32} />,
                title: 'Urgent Service',
                description: 'Same day service - Rush processing for critical deadlines.',
                timeline: 'Same Day'
              },
              {
                icon: <Clock size={32} />,
                title: 'On-Demand Service',
                description: 'Under 2 hours - Emergency processing for immediate needs.',
                timeline: '< 2 Hours'
              }
            ].map((service) => (
              <div key={service.title} style={{
                backgroundColor: 'var(--bg-card)',
                padding: '2rem',
                borderRadius: '12px',
                border: '1px solid var(--border-medium)',
                textAlign: 'center'
              }}>
                <div style={{ color: 'var(--brand-primary)', marginBottom: '1rem', display: 'flex', justifyContent: 'center' }}>
                  {service.icon}
                </div>
                <h3 className="heading-3" style={{ marginBottom: '0.5rem' }}>{service.title}</h3>
                <div style={{ 
                  color: 'var(--brand-primary)', 
                  fontWeight: '600', 
                  fontSize: '1.1rem',
                  marginBottom: '1rem'
                }}>
                  {service.timeline}
                </div>
                <p className="body-text" style={{ color: 'var(--text-secondary)' }}>{service.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Coverage Area */}
      <section style={{ backgroundColor: 'var(--bg-card)', padding: '80px 0' }}>
        <div className="container">
          <div style={{ textAlign: 'center', marginBottom: '3rem' }}>
            <h2 className="heading-1" style={{ marginBottom: '1rem' }}>
              Palm Springs Service Coverage
            </h2>
            <p className="body-large" style={{ color: 'var(--text-secondary)', maxWidth: '600px', margin: '0 auto' }}>
              Comprehensive coverage from Palm Springs throughout the entire Coachella Valley region.
            </p>
          </div>

          <div style={{
            backgroundColor: 'var(--bg-page)',
            padding: '2rem',
            borderRadius: '12px',
            border: '1px solid var(--border-medium)',
            textAlign: 'center'
          }}>
            <h3 className="heading-3" style={{ marginBottom: '1.5rem' }}>
              Cities Served from Palm Springs Base
            </h3>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
              gap: '1rem',
              marginBottom: '2rem'
            }}>
              {[
                'Palm Springs', 'Cathedral City', 'Palm Desert', 'Desert Hot Springs',
                'Rancho Mirage', 'La Quinta', 'Indian Wells', 'Indio',
                'Coachella', 'Thousand Palms', 'Bermuda Dunes', 'North Palm Springs'
              ].map((city, index) => (
              <div key={city} style={{
                  padding: '0.75rem',
                  backgroundColor: index === 0 ? 'var(--brand-primary)' : 'var(--bg-card)',
                  borderRadius: '6px',
                  border: '1px solid var(--border-light)'
                }}>
                  <span style={{ 
                    color: index === 0 ? 'white' : 'var(--text-primary)',
                    fontWeight: index === 0 ? '600' : 'normal'
                  }}>{city}</span>
                </div>
              ))}
            </div>
            <p style={{ color: 'var(--text-secondary)', fontSize: '0.9rem' }}>
              Plus statewide California service and all Riverside County communities. Our regional expertise extends from the <Link to="/cities/riverside" style={{ color: 'var(--brand-primary)', textDecoration: 'none' }}>county courthouse in Riverside</Link> to luxury communities like <Link to="/cities/rancho-mirage" style={{ color: 'var(--brand-primary)', textDecoration: 'none' }}>Rancho Mirage</Link> and <Link to="/cities/la-quinta" style={{ color: 'var(--brand-primary)', textDecoration: 'none' }}>La Quinta</Link>.
            </p>
          </div>
        </div>
      </section>

      {/* Related Services Section */}
      <section style={{ backgroundColor: 'var(--bg-card)', padding: '60px 0' }}>
        <div className="container">
          <div style={{ textAlign: 'center', marginBottom: '3rem' }}>
            <h2 className="heading-2" style={{ marginBottom: '1rem' }}>
              Comprehensive Legal Services in Palm Springs
            </h2>
            <p className="body-medium" style={{ color: 'var(--text-secondary)', maxWidth: '600px', margin: '0 auto' }}>
              Beyond process serving, we offer complete legal document support services for the Palm Springs area.
            </p>
          </div>
          
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
            gap: '1.5rem',
            marginBottom: '2rem'
          }}>
            <Link to="/attorney-service" style={{ textDecoration: 'none' }}>
              <div style={{
                backgroundColor: 'var(--bg-page)',
                padding: '1.5rem',
                borderRadius: '8px',
                border: '1px solid var(--border-medium)',
                height: '100%',
                transition: 'all 0.3s ease'
              }} className="service-link-card">
                <h3 style={{ color: 'var(--brand-primary)', marginBottom: '0.75rem', fontSize: '1.1rem' }}>
                  Attorney Services Platform
                </h3>
                <p style={{ color: 'var(--text-secondary)', fontSize: '0.9rem', lineHeight: '1.5', margin: 0 }}>
                  Dedicated support for Palm Springs law firms with high-volume processing and priority service.
                </p>
              </div>
            </Link>
            
            <Link to="/court-filing" style={{ textDecoration: 'none' }}>
              <div style={{
                backgroundColor: 'var(--bg-page)',
                padding: '1.5rem',
                borderRadius: '8px',
                border: '1px solid var(--border-medium)',
                height: '100%',
                transition: 'all 0.3s ease'
              }} className="service-link-card">
                <h3 style={{ color: 'var(--brand-primary)', marginBottom: '0.75rem', fontSize: '1.1rem' }}>
                  Court Filing & E-Filing
                </h3>
                <p style={{ color: 'var(--text-secondary)', fontSize: '0.9rem', lineHeight: '1.5', margin: 0 }}>
                  Same-day filing at Palm Springs courthouse on Tahquitz Canyon Way, plus Indio Larson Justice Center for major cases.
                </p>
              </div>
            </Link>
            
            <Link to="/skip-tracing" style={{ textDecoration: 'none' }}>
              <div style={{
                backgroundColor: 'var(--bg-page)',
                padding: '1.5rem',
                borderRadius: '8px',
                border: '1px solid var(--border-medium)',
                height: '100%',
                transition: 'all 0.3s ease'
              }} className="service-link-card">
                <h3 style={{ color: 'var(--brand-primary)', marginBottom: '0.75rem', fontSize: '1.1rem' }}>
                  Skip Tracing Services
                </h3>
                <p style={{ color: 'var(--text-secondary)', fontSize: '0.9rem', lineHeight: '1.5', margin: 0 }}>
                  Professional people location services for Palm Springs resort and business communities.
                </p>
              </div>
            </Link>
          </div>
          
          <div style={{ textAlign: 'center' }}>
            <Link to="/service-areas" style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '0.5rem',
              padding: '1rem 2rem',
              backgroundColor: 'var(--brand-primary)',
              color: 'white',
              borderRadius: '8px',
              textDecoration: 'none',
              fontWeight: '600'
            }}>
              View All Service Areas
              <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" style={{ backgroundColor: 'var(--bg-page)', padding: '80px 0' }}>
        <div className="container">
          <div style={{ textAlign: 'center', marginBottom: '3rem' }}>
            <h2 className="heading-1" style={{ marginBottom: '1rem' }}>
              Contact Us for Palm Springs Services
            </h2>
            <p className="body-large" style={{ color: 'var(--text-secondary)', maxWidth: '600px', margin: '0 auto' }}>
              Ready to get started? Contact Legal Document Specialist for professional service in Palm Springs.
            </p>
          </div>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
            gap: '2rem',
            maxWidth: '800px',
            margin: '0 auto'
          }}>
            {/* Contact Information */}
            <div style={{
              backgroundColor: 'var(--bg-card)',
              padding: '2rem',
              borderRadius: '12px',
              border: '1px solid var(--border-medium)'
            }}>
              <h3 className="heading-3" style={{ marginBottom: '1.5rem' }}>Contact Information</h3>
              
              <div style={{ marginBottom: '1.5rem', display: 'flex', alignItems: 'center' }}>
                <Phone size={20} color="var(--brand-primary)" style={{ marginRight: '1rem' }} />
                <div>
                  <div style={{ fontWeight: '600', color: 'var(--text-primary)' }}>{companyInfo.phone}</div>
                  <div style={{ color: 'var(--text-secondary)', fontSize: '0.9rem' }}>Monday-Friday 9AM-5PM PST</div>
                </div>
              </div>

              <div style={{ marginBottom: '1.5rem', display: 'flex', alignItems: 'center' }}>
                <Mail size={20} color="var(--brand-primary)" style={{ marginRight: '1rem' }} />
                <div>
                  <div style={{ fontWeight: '600', color: 'var(--text-primary)' }}>{companyInfo.email}</div>
                  <div style={{ color: 'var(--text-secondary)', fontSize: '0.9rem' }}>24/7 email support</div>
                </div>
              </div>

              <div style={{ display: 'flex', alignItems: 'center' }}>
                <MapPin size={20} color="var(--brand-primary)" style={{ marginRight: '1rem' }} />
                <div>
                  <div style={{ fontWeight: '600', color: 'var(--text-primary)' }}>Legal Document Specialist</div>
                  <div style={{ color: 'var(--text-secondary)', fontSize: '0.9rem' }}>{companyInfo.address}</div>
                </div>
              </div>
            </div>

            {/* Service Request */}
            <div style={{
              backgroundColor: 'var(--bg-card)',
              padding: '2rem',
              borderRadius: '12px',
              border: '1px solid var(--border-medium)',
              textAlign: 'center'
            }}>
              <h3 className="heading-3" style={{ marginBottom: '1.5rem' }}>Palm Springs Service Request</h3>
              <p style={{ color: 'var(--text-secondary)', marginBottom: '2rem' }}>
                Choose your service option for Palm Springs:
              </p>

              <div style={{ marginBottom: '1.5rem' }}>
                <Link to="/place-order" className="btn-primary" style={{ 
                  textDecoration: 'none',
                  display: 'block',
                  marginBottom: '1rem'
                }}>
                  Place Order Online
                </Link>
                <Link to="/quote" className="btn-secondary" style={{ 
                  textDecoration: 'none',
                  display: 'block'
                }}>
                  Request Free Quote
                </Link>
              </div>

              <div style={{ 
                backgroundColor: 'var(--bg-page)', 
                padding: '1rem', 
                borderRadius: '6px',
                border: '1px solid var(--border-light)'
              }}>
                <p style={{ color: 'var(--text-secondary)', fontSize: '0.9rem', margin: 0 }}>
                  <strong>Serving the Heart of Coachella Valley</strong><br />
                  Professional • Registered • Bonded • Insured
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Related Cities Section */}
      <RelatedCities currentCity="palm-springs" region="coachella-valley" />

      <Footer />

      <style>{`
        .service-link-card:hover {
          transform: translateY(-2px);
          border-color: var(--brand-primary);
          box-shadow: 0 4px 12px rgba(44, 90, 160, 0.15);
        }
        
        @media (max-width: 767px) {
          div[style*="gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))'"] {
            grid-template-columns: 1fr !important;
          }
        }
      `}</style>
    </div>
  );
};

export default PalmSpringsPage;